import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface UseFeatureGateCacheCleanupOptions {
  isEnabled: boolean;
  featureKey: string;
  queryKeysToInvalidate?: Array<string | unknown[]>; // Query keys to clear when disabled
}

/**
 * Hook to clean up cached data when a feature becomes disabled
 * Prevents stale data from showing after feature access is revoked
 * 
 * This solves the two-layer cache problem:
 * - Layer 1: Feature flags cache (handled by useFeatureFlags refetchOnWindowFocus)
 * - Layer 2: Page data cache (handled by this hook)
 * 
 * When a feature transitions from enabled → disabled, this hook:
 * 1. Clears all specified query keys (page-specific data)
 * 2. Optionally clears any queries containing the feature key
 * 
 * @example
 * useFeatureGateCacheCleanup({
 *   isEnabled: true,
 *   featureKey: 'portfolio_command_center',
 *   queryKeysToInvalidate: ['priority-actions-optimized', 'portfolio-category-config']
 * });
 */
export const useFeatureGateCacheCleanup = ({
  isEnabled,
  featureKey,
  queryKeysToInvalidate = []
}: UseFeatureGateCacheCleanupOptions) => {
  const queryClient = useQueryClient();
  const previousEnabledRef = useRef<boolean | null>(null);
  
  useEffect(() => {
    // Skip on initial mount (no previous state to compare)
    if (previousEnabledRef.current === null) {
      previousEnabledRef.current = isEnabled;
      return;
    }
    
    // Detect transition from enabled → disabled
    const wasEnabled = previousEnabledRef.current;
    const isNowDisabled = !isEnabled;
    
    if (wasEnabled && isNowDisabled) {
      console.log(`🧹 [FeatureGate] Feature "${featureKey}" disabled - clearing cached data`);
      
      // Clear all specified query keys
      queryKeysToInvalidate.forEach((queryKey) => {
        const keyArray = Array.isArray(queryKey) ? queryKey : [queryKey];
        queryClient.removeQueries({ queryKey: keyArray });
        console.log(`  ↳ Cleared cache for: ${JSON.stringify(keyArray)}`);
      });
      
      // Also clear any queries that contain the feature key in their queryKey
      // This catches related queries that might have been missed
      queryClient.removeQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.some(k => 
            typeof k === 'string' && k.includes(featureKey)
          );
        }
      });
      
      console.log(`✅ [FeatureGate] Cleared ${queryKeysToInvalidate.length} specified query caches for "${featureKey}"`);
    }
    
    // Update previous state
    previousEnabledRef.current = isEnabled;
  }, [isEnabled, featureKey, queryKeysToInvalidate, queryClient]);
};
